import React from 'react';
import { X, User } from 'lucide-react';
import { Modal } from '../../Modal';
import { cn } from '../../../../utils/cn';
import { useAuth } from '../../../../hooks/useAuth';
import { usePremium } from '../../../../hooks/usePremium';
import { useInstitutions } from '../../../../hooks/useInstitutions';
import { useAccessControl } from '../../../../contexts/AccessControlContext';
import { usePremiumContext } from '../../../../contexts/PremiumContext';
import { useTheme } from '../../../../contexts/ThemeContext';
import { TermsModal } from '../TermsModal';
import { EmailVerificationModal } from '../EmailVerificationModal';

import { useAuthForm } from './hooks/useAuthForm';
import { useAuthValidation } from './hooks/useAuthValidation';
import { useUserProfile } from './hooks/useUserProfile';
import { AuthForm } from './components/AuthForm';
import { LoggedInView } from './components/LoggedInView';
import { AuthErrorDisplay } from './components/AuthErrorDisplay';
import { SignInBenefits } from './components/SignInBenefits';
import { ThemeToggle } from '../../ThemeToggle';

const CAPTCHA_REQUIRED_MESSAGE = 'Please complete the CAPTCHA challenge';

export interface UserModalProps {
  isOpen: boolean;
  onClose: () => void;
  className?: string;
}

export const UserModal: React.FC<UserModalProps> = ({
  isOpen,
  onClose,
  className
}) => {
  // Contexts
  const {
    user,
    session,
    signIn,
    signUp,
    signOut,
    signInWithGoogle,
    signInWithApple,
    loading: authLoading,
    updateUsername,
    resendVerificationEmail,
    isEmailVerified,
    checkUsernameAvailability,
    checkEmailAvailability,
    checkEmailConfirmationStatus,
    acceptTerms
  } = useAuth();

  const { isPremium, planType, loading: premiumLoading } = usePremium();
  const { premiumMode } = useAccessControl();
  const { isStudent, isCorporate } = usePremiumContext();
  const { theme } = useTheme();
  const { institutions, toggleRepresent } = useInstitutions({ userId: user?.id, isStudent, isCorporate, enabled: isOpen });

  // Auth validation hook
  const {
    usernameAvailable,
    checkingUsername,
    setUsernameAvailable,
    checkUsername,
    emailValid,
    emailAvailable,
    checkingEmail,
    setEmailAvailable,
    checkEmail,
    resetEmailValidation,
  } = useAuthValidation({
    checkUsernameAvailability,
    checkEmailAvailability,
  });

  // Auth form hook
  const authForm = useAuthForm({
    session,
    authActions: {
      signIn,
      signUp,
      signOut,
      signInWithGoogle,
      signInWithApple,
      resendVerificationEmail,
      checkUsernameAvailability,
      checkEmailConfirmationStatus,
      acceptTerms,
    },
    isOpen,
    theme,
  });

  // User profile hook
  const {
    userProfile,
    editingUsername,
    newUsername,
    usernameLoading,
    setEditingUsername,
    setNewUsername,
    handleUpdateUsername,
    cancelUsernameEdit,
  } = useUserProfile({
    user,
    isOpen,
    updateUsername,
  });

  // Handle username save with validation
  const handleSaveUsername = async () => {
    const result = await handleUpdateUsername(checkingUsername, usernameAvailable);
    if (result.success) {
      authForm.setSuccessMessage('Username updated!');
      setUsernameAvailable(null);
    } else if (result.error) {
      authForm.setError(result.error);
    }
  };

  // Handle username cancel
  const handleCancelUsername = () => {
    cancelUsernameEdit();
    setUsernameAvailable(null);
  };

  // Handle username change with availability check
  const handleUsernameChange = (value: string) => {
    setNewUsername(value);
    checkUsername(value);
  };

  // Handle form submission
  const handleFormSubmit = (e: React.FormEvent) => {
    authForm.handleEmailAuth(
      e,
      emailAvailable,
      setEmailAvailable,
      usernameAvailable,
      setUsernameAvailable
    );
  };

  // Handle CAPTCHA success
  const handleCaptchaSuccess = (token: string) => {
    authForm.setCaptchaToken(token);
    if (authForm.error === CAPTCHA_REQUIRED_MESSAGE) {
      authForm.setError('');
    }
  };

  // Handle resend verification from error display
  const handleResendFromError = async () => {
    await authForm.handleResendVerification();
  };

  const isLoggedOut = !user;

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={isLoggedOut ? undefined : '👤 User Profile'}
        description={isLoggedOut ? undefined : 'Manage your account and view your progress'}
        showCloseButton={!isLoggedOut}
        className={cn('max-w-md', className)}
      >
        <div className="space-y-6">
          {/* Custom warm header for unauthenticated view */}
          {isLoggedOut && (
            <div className="relative flex flex-col items-center pt-2 pb-1">
              <button
                onClick={onClose}
                className="absolute top-2 right-0 p-1.5 rounded-full text-[var(--auth-text-secondary)] hover:text-foreground hover:bg-[var(--auth-border-warm)]/30 transition-colors"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
              <div
                className="w-14 h-14 rounded-full flex items-center justify-center mb-3"
                style={{ background: `linear-gradient(135deg, var(--auth-accent-gradient-from), var(--auth-accent-gradient-to))` }}
              >
                <User className="h-7 w-7 text-white" />
              </div>
              <h2 className="font-serif text-2xl font-semibold text-foreground">User Profile</h2>
              <p className="text-sm text-[var(--auth-text-secondary)] mt-1">Sign in to track your progress</p>
            </div>
          )}

          {user ? (
            <LoggedInView
              user={user}
              userProfile={userProfile}
              isPremium={isPremium}
              planType={planType}
              premiumMode={premiumMode}
              premiumLoading={premiumLoading}
              isEmailVerified={isEmailVerified}
              authLoading={authLoading}
              successMessage={authForm.successMessage}
              editingUsername={editingUsername}
              newUsername={newUsername}
              usernameLoading={usernameLoading}
              checkingUsername={checkingUsername}
              usernameAvailable={usernameAvailable}
              onStartEditUsername={() => setEditingUsername(true)}
              onSaveUsername={handleSaveUsername}
              onCancelUsername={handleCancelUsername}
              onUsernameChange={handleUsernameChange}
              onLogout={authForm.handleLogout}
              onResendVerification={authForm.handleResendVerification}
              onClose={onClose}
              resendingVerification={authForm.resendingVerification}
              institutions={institutions}
              onToggleRepresent={toggleRepresent}
            />
          ) : (
            <>
            <SignInBenefits />
            <div className="flex items-center justify-between w-full">
              <span className="text-sm font-semibold text-foreground">Theme</span>
              <ThemeToggle />
            </div>
            <AuthForm
              mode={authForm.mode}
              email={authForm.email}
              password={authForm.password}
              confirmPassword={authForm.confirmPassword}
              username={authForm.username}
              authLoading={authLoading}
              theme={theme}
              onEmailChange={authForm.setEmail}
              onPasswordChange={authForm.setPassword}
              onConfirmPasswordChange={authForm.setConfirmPassword}
              onUsernameChange={authForm.setUsername}
              showPassword={authForm.showPassword}
              showConfirmPassword={authForm.showConfirmPassword}
              onToggleShowPassword={() => authForm.setShowPassword(!authForm.showPassword)}
              onToggleShowConfirmPassword={() => authForm.setShowConfirmPassword(!authForm.showConfirmPassword)}
              checkingEmail={checkingEmail}
              emailValid={emailValid}
              emailAvailable={emailAvailable}
              onCheckEmail={checkEmail}
              shouldRenderCaptcha={authForm.shouldRenderCaptcha}
              captchaResetCounter={authForm.captchaResetCounter}
              onCaptchaSuccess={handleCaptchaSuccess}
              onCaptchaError={() => authForm.setCaptchaToken(null)}
              onCaptchaExpire={() => authForm.setCaptchaToken(null)}
              termsAccepted={authForm.termsAccepted}
              termsError={authForm.termsError}
              onTermsChange={(checked) => {
                authForm.setTermsAccepted(checked);
                authForm.setTermsError('');
              }}
              onViewTerms={() => authForm.setShowTermsModal(true)}
              successMessage={authForm.successMessage}
              hasPendingCheckout={authForm.hasPendingCheckout}
              googleButtonLabel={authForm.googleButtonLabel}
              appleButtonLabel={authForm.appleButtonLabel}
              onSubmit={handleFormSubmit}
              onGoogleLogin={authForm.handleGoogleLogin}
              onAppleLogin={authForm.handleAppleLogin}
              onModeSwitch={() => authForm.handleModeSwitch(resetEmailValidation)}
              onClose={onClose}
            />
            </>
          )}

          <AuthErrorDisplay
            error={authForm.error}
            errorType={authForm.errorType}
            recoverySuggestions={authForm.recoverySuggestions}
            showLoginSuggestion={authForm.showLoginSuggestion}
            showResendVerification={authForm.showResendVerification}
            resendingVerification={authForm.resendingVerification}
            onTryLogin={authForm.handleTryLogin}
            onResendVerification={handleResendFromError}
          />
        </div>

        {/* Terms Modal */}
        <TermsModal
          isOpen={authForm.showTermsModal}
          onClose={() => authForm.setShowTermsModal(false)}
          showAcceptButton={false}
        />
      </Modal>

      <EmailVerificationModal
        isOpen={authForm.showEmailVerificationModal}
        onClose={() => authForm.setShowEmailVerificationModal(false)}
        email={authForm.verificationEmail}
      />
    </>
  );
};

export default UserModal;
